// ============================================================================
// SECURITY TYPES - Cheat Protection System
// ============================================================================
// All interfaces for the security/flag-based cheat detection system.
// This system creates FLAGS for human review - NO auto-bans.
// ============================================================================

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export type SecurityFlagType = 
  | 'collusion'
  | 'multi_account'
  | 'bot'
  | 'chip_dumping'
  | 'same_ip_table'
  | 'suspicious_timing'
  | 'unusual_pattern';

export type FlagSeverity = 'low' | 'medium' | 'high' | 'critical';

export type FlagStatus = 'new' | 'reviewing' | 'resolved' | 'dismissed';

export type FlagResolution = 'confirmed' | 'false_positive' | 'inconclusive';

export type ActionTaken = 'none' | 'warning' | 'hold' | 'suspend' | 'ban';

export type AgentTier = 'standard' | 'premium' | 'vip';

// Confidence thresholds for flag creation
export const FLAG_THRESHOLDS = {
  // Multi-account detection
  SAME_DEVICE_2_ACCOUNTS: 80,
  SAME_DEVICE_3_PLUS_ACCOUNTS: 90,
  SAME_IP_TABLE: 70,
  
  // Collusion detection
  COLLUSION_MEDIUM: 60,
  COLLUSION_HIGH: 80,
  
  // Bot detection
  BOT_MEDIUM: 70,
  BOT_HIGH: 85,
  
  // Chip dumping detection
  CHIP_DUMP_MEDIUM: 65,
  CHIP_DUMP_HIGH: 80,
} as const;

// Signal weights for collusion scoring
export const COLLUSION_SIGNAL_WEIGHTS = {
  SAME_TABLE_FREQUENCY: 15,       // Always at same table
  ONE_ALWAYS_FOLDS: 25,           // One player always folds to the other
  SOFT_PLAY_HEADS_UP: 15,         // Check down when heads-up
  CHIP_TRANSFER_PATTERN: 20,      // Consistent chip flow one direction
  SAME_IP: 25,                    // Same IP address
  SAME_DEVICE: 30,                // Same device fingerprint
  SUSPICIOUS_TIMING: 10,          // Coordinated action timing
} as const;

// ============================================================================
// SECURITY FLAG
// ============================================================================

export interface SecurityFlag {
  id: string;
  type: SecurityFlagType;
  severity: FlagSeverity;
  confidence: number;                    // 0-100
  status: FlagStatus;
  
  // Players involved
  flaggedUserIds: string[];              // All players involved
  primaryUserId: string;                 // Main suspect
  relatedUserId?: string;                // For pairs (collusion, multi-account)
  
  // Evidence
  evidence: FlagEvidence;
  
  // Timestamps
  createdAt: Timestamp;
  createdBy: 'system';
  
  // Assignment & resolution
  assignedTo?: string;                   // Admin userId
  assignedAt?: Timestamp;
  resolvedAt?: Timestamp;
  resolvedBy?: string;
  resolution?: FlagResolution;
  resolutionNotes?: string;
  actionTaken?: ActionTaken;
}

export interface FlagEvidence {
  signals: string[];                     // Which signals triggered
  dataPoints: EvidenceDataPoint[];       // Supporting data
  tableIds?: string[];
  handNumbers?: number[];
  summary?: string;                      // Human-readable summary
}

export interface EvidenceDataPoint {
  type: string;
  description: string;
  value: number | string | boolean;
  weight?: number;
  timestamp?: Timestamp;
}

// ============================================================================
// DEVICE FINGERPRINT
// ============================================================================

export interface DeviceFingerprint {
  hash: string;                          // SHA-256 hash of fingerprint
  userIds: string[];                     // All users seen with this fingerprint
  components: FingerprintComponents;
  firstSeenAt: Timestamp;
  lastSeenAt: Timestamp;
  firstSeenUserId: string;
  flagged: boolean;
}

export interface FingerprintComponents {
  userAgent: string;
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
  colorDepth: number;
  hardwareConcurrency: number;
  canvasHash?: string;
  webglVendor?: string;
  webglRenderer?: string;
  audioFingerprint?: string;
  fonts?: string[];
}

// Raw fingerprint data before hashing
export interface RawFingerprintData {
  userAgent: string;
  screen: {
    width: number;
    height: number;
    colorDepth: number;
  };
  timezone: string;
  language: string;
  platform: string;
  hardwareConcurrency: number;
  canvas?: string;
  webgl?: {
    vendor: string;
    renderer: string;
  };
}

// ============================================================================
// IP HISTORY
// ============================================================================

export interface IPHistory {
  ipHash: string;                        // Hashed IP (privacy)
  userIds: string[];                     // All users from this IP
  tableJoins: IPTableJoin[];
  firstSeenAt: Timestamp;
  lastSeenAt: Timestamp;
  flagged: boolean;
}

export interface IPTableJoin {
  tableId: string;
  odD: string;
  timestamp: Timestamp;
}

// ============================================================================
// PLAYER RELATIONSHIPS
// ============================================================================

export interface PlayerRelationship {
  player1Id: string;
  player2Id: string;
  
  // Table/hand statistics
  tablesPlayedTogether: number;
  handsPlayedTogether: number;
  
  // Fold patterns
  player1FoldsToPlayer2: number;
  player2FoldsToPlayer1: number;
  
  // Win patterns
  player1WinsVsPlayer2: number;
  player2WinsVsPlayer1: number;
  
  // Chip flow
  netChipFlow: number;                   // Positive = flows to player2
  
  // Timing
  lastPlayedTogether: Timestamp;
  
  // Suspicious indicators
  sameIpOccurrences: number;
  sameDeviceOccurrences: number;
  
  // Calculated scores
  collusionScore: number;
  lastAnalyzed: Timestamp;
}

// ============================================================================
// ACTION TIMING (Bot Detection)
// ============================================================================

export interface ActionTimingRecord {
  playerId: string;
  recentActions: ActionTiming[];         // Rolling window of last 500 actions
  stats: TimingStats;
  botScore: number;
  lastAnalyzed: Timestamp;
}

export interface ActionTiming {
  tableId: string;
  handNumber: number;
  actionType: string;                    // fold, check, call, raise, all-in
  thinkTimeMs: number;
  timestamp: Timestamp;
  preActionPotSize?: number;
  decisionComplexity?: 'simple' | 'medium' | 'complex';
}

export interface TimingStats {
  avgThinkTime: number;
  stdDevThinkTime: number;
  minThinkTime: number;
  maxThinkTime: number;
  actionsAnalyzed: number;
  avgThinkTimeByAction: {
    [actionType: string]: number;
  };
  instantActionRate: number;             // % of actions < 500ms
  suspiciouslyFastRate: number;          // % of actions < 200ms
}

// ============================================================================
// CHIP FLOWS (Chip Dumping Detection)
// ============================================================================

export interface ChipFlowRecord {
  player1Id: string;
  player2Id: string;
  
  // Flow totals
  totalFlowToPlayer1: number;
  totalFlowToPlayer2: number;
  netFlow: number;                       // Positive = to player2
  
  // Statistics
  handsPlayed: number;
  largestSingleTransfer: number;
  avgTransferSize: number;
  
  // Suspicious hands
  suspiciousHands: SuspiciousHand[];
  
  // Scores
  dumpScore: number;
  lastUpdated: Timestamp;
}

export interface SuspiciousHand {
  tableId: string;
  handNumber: number;
  amount: number;
  reason: string;
  timestamp: Timestamp;
}

// ============================================================================
// RISK SCORE
// ============================================================================

export interface PlayerRiskScore {
  odD: string;
  overallRisk: number;                   // 0-100
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  
  // Component scores
  multiAccountRisk: number;
  collusionRisk: number;
  botRisk: number;
  chipDumpRisk: number;
  
  // Flag counts
  totalFlags: number;
  activeFlags: number;
  resolvedFlags: number;
  confirmedFlags: number;
  
  // History
  lastUpdated: Timestamp;
  riskHistory: RiskHistoryEntry[];
}

export interface RiskHistoryEntry {
  date: Timestamp;
  overallRisk: number;
  reason?: string;
}

// ============================================================================
// SECURITY ALERT (Admin Notifications)
// ============================================================================

export interface SecurityAlert {
  id: string;
  type: 'new_flag' | 'critical_flag' | 'pattern_detected' | 'risk_spike';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  
  title: string;
  message: string;
  
  // Related data
  flagId?: string;
  odD?: string;
  userIds?: string[];
  
  // Status
  read: boolean;
  readAt?: Timestamp;
  readBy?: string;
  
  // Timestamps
  createdAt: Timestamp;
  expiresAt?: Timestamp;
}

// ============================================================================
// SECURITY CONTEXT (Captured at signup/login)
// ============================================================================

export interface SecurityContext {
  ip: string;
  ipHash: string;
  userAgent: string;
  deviceFingerprint: string;
  timezone: string;
  screenResolution: string;
  language: string;
  platform: string;
  timestamp: number;
}

// ============================================================================
// USER SECURITY PROFILE (Extended user data)
// ============================================================================

export interface UserSecurityProfile {
  odD: string;
  
  // Signup data
  signupIp?: string;
  signupIpHash?: string;
  signupDeviceFingerprint?: string;
  signupUserAgent?: string;
  signupTimezone?: string;
  signupAt?: Timestamp;
  
  // Last login data
  lastLoginIp?: string;
  lastLoginIpHash?: string;
  lastLoginDeviceFingerprint?: string;
  lastLoginAt?: Timestamp;
  
  // Known devices
  knownDevices?: string[];               // Fingerprint hashes
  knownIps?: string[];                   // IP hashes
  
  // Flags
  flagCount?: number;
  activeFlags?: number;
  
  // Risk
  riskScore?: number;
  riskLevel?: 'low' | 'medium' | 'high' | 'critical';
}

// ============================================================================
// SERVICE FUNCTION TYPES
// ============================================================================

// Create flag request
export interface CreateFlagRequest {
  type: SecurityFlagType;
  severity: FlagSeverity;
  confidence: number;
  primaryUserId: string;
  relatedUserId?: string;
  evidence: FlagEvidence;
}

// Flag query options
export interface FlagQueryOptions {
  type?: SecurityFlagType;
  severity?: FlagSeverity;
  status?: FlagStatus;
  odD?: string;
  assignedTo?: string;
  limit?: number;
  orderBy?: 'createdAt' | 'confidence' | 'severity';
  orderDirection?: 'asc' | 'desc';
}

// Flag update request
export interface UpdateFlagRequest {
  status?: FlagStatus;
  assignedTo?: string;
  resolution?: FlagResolution;
  resolutionNotes?: string;
  actionTaken?: ActionTaken;
}

// Hand participation data (for collusion detection)
export interface HandParticipation {
  tableId: string;
  handNumber: number;
  playerIds: string[];
  winnerIds: string[];
  betHistory: PlayerBetSummary[];
  timestamp: Timestamp;
}

export interface PlayerBetSummary {
  playerId: string;
  totalBet: number;
  folded: boolean;
  foldedToPlayerId?: string;             // Who they folded to (if heads-up)
  won: boolean;
  amountWon: number;
}

// Chip transfer data (for chip dumping detection)
export interface ChipTransfer {
  tableId: string;
  handNumber: number;
  fromPlayers: TransferParticipant[];
  toPlayers: TransferParticipant[];
  timestamp: Timestamp;
}

export interface TransferParticipant {
  playerId: string;
  amount: number;
}

// Table join data (for collusion detection)
export interface TableJoinData {
  tableId: string;
  playerId: string;
  currentPlayerIds: string[];
  timestamp: Timestamp;
  playerIp?: string;
}

// ============================================================================
// ADMIN DASHBOARD TYPES
// ============================================================================

export interface SecurityDashboardStats {
  // Flag counts
  newFlags: number;
  reviewingFlags: number;
  resolvedToday: number;
  resolvedThisWeek: number;
  
  // By type
  collusionFlags: number;
  multiAccountFlags: number;
  botFlags: number;
  chipDumpFlags: number;
  
  // By severity
  criticalFlags: number;
  highFlags: number;
  mediumFlags: number;
  lowFlags: number;
  
  // Trends
  flagsLast24h: number;
  flagsLast7d: number;
  flagsLast30d: number;
  
  // Players
  flaggedPlayers: number;
  highRiskPlayers: number;
}

export interface FlagListItem {
  id: string;
  type: SecurityFlagType;
  severity: FlagSeverity;
  confidence: number;
  status: FlagStatus;
  primaryUsername?: string;
  relatedUsername?: string;
  primaryUserId: string;
  relatedUserId?: string;
  createdAt: Timestamp;
  assignedToName?: string;
  evidenceSummary: string;
}

// ============================================================================
// AGENT DASHBOARD TYPES (Read-only referral security view)
// ============================================================================

export interface ReferralSecuritySummary {
  totalReferrals: number;
  cleanReferrals: number;
  flaggedReferrals: number;
  actionedReferrals: number;
}

export interface FlaggedReferral {
  odD: string;
  username: string;
  flagType: SecurityFlagType;
  severity: FlagSeverity;
  status: FlagStatus;
  createdAt: Timestamp;
}